import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';

import { RootState } from '../..';
import { IUser } from '../../UserSlice';
import { IGroup } from '../GroupsSlice';
import { IRole } from '../RolesSlice';
import {
  adminCreateCoursePermissionTemplate,
  adminGetCoursePermissionTemplates,
  adminGetCoursePermissionTemplatesForDropDown,
  adminUpdateCoursePermissionTemplate,
  adminVerifyCoursePermissionTemplateDelete,
  adminDeleteCoursePermissionTemplate,
} from './adminApi';

// COURSE PERMISSION TEMPLATES
export const COURSE_PERMISSION_TEMPLATE_ROLE_CRITERIAS = {
  ALL: 'ALL',
  INCLUDE: 'INCLUDE',
  EXCLUDE: 'EXCLUDE',
} as const;

export const COURSE_PERMISSION_TEMPLATE_GROUP_CRITERIAS = {
  ALL: 'ALL',
  INCLUDE: 'INCLUDE',
  EXCLUDE: 'EXCLUDE',
} as const;

export const COURSE_PERMISSION_TEMPLATE_USER_CRITERIAS = {
  ALL: 'ALL',
  INCLUDE: 'INCLUDE',
  EXCLUDE: 'EXCLUDE',
} as const;

export type TCoursePermissionTemplateGroupCriterias = typeof COURSE_PERMISSION_TEMPLATE_GROUP_CRITERIAS[keyof typeof COURSE_PERMISSION_TEMPLATE_GROUP_CRITERIAS];
export type TCoursePermissionTemplateRoleCriterias = typeof COURSE_PERMISSION_TEMPLATE_ROLE_CRITERIAS[keyof typeof COURSE_PERMISSION_TEMPLATE_ROLE_CRITERIAS];
export type TCoursePermissionTemplateUserCriterias = typeof COURSE_PERMISSION_TEMPLATE_USER_CRITERIAS[keyof typeof COURSE_PERMISSION_TEMPLATE_USER_CRITERIAS];

export interface ICoursePermissionTemplate {
  _id: string;
  name: string;
  group: boolean;
  role: boolean;
  user: boolean;
  createdBy: string | IUser;
  updatedBy: string | IUser;
  groupCriteria?: TCoursePermissionTemplateGroupCriterias;
  groups?: string[] | IGroup[];
  roleCriteria?: TCoursePermissionTemplateRoleCriterias;
  roles?: string[] | IRole[];
  userCriteria?: TCoursePermissionTemplateUserCriterias;
  users?: string[] | IUser[];
}

export interface ICoursePermissionTemplateDeleteVerificationData {
  coursesCount: number;
}

export interface IInitialCoursePermissionTemplatesState {
  status: 'idle' | 'loading' | 'rejected';
  // Admin
  adminCoursePermissionTemplateListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ICoursePermissionTemplate>;
  };
  adminCoursePermissionTemplateListDataForDropdown?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ICoursePermissionTemplate>;
  };
  adminCoursePermissionTemplateDeleteVerificationData?: ICoursePermissionTemplateDeleteVerificationData;
}

const initialState: IInitialCoursePermissionTemplatesState = {
  status: 'idle',
  //  COURSE PERMISSION TEMPLATES
  adminCoursePermissionTemplateListData: undefined,
  adminCoursePermissionTemplateListDataForDropdown: undefined,
  adminCoursePermissionTemplateDeleteVerificationData: undefined,
};

export const SettingsSlice = createSlice({
  initialState,
  name: 'settings',
  reducers: {
    //  COURSE PERMISSION TEMPLATES
    resetAdminCoursePermissionTemplateListData: (state) => {
      state.adminCoursePermissionTemplateListData = undefined;
    },
    resetAdminCoursePermissionTemplateListDataForDropdown: (state) => {
      state.adminCoursePermissionTemplateListDataForDropdown = undefined;
    },
    resetAdminCoursePermissionTemplateDeleteVerificationData: (state) => {
      state.adminCoursePermissionTemplateDeleteVerificationData = undefined;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(adminGetCoursePermissionTemplates.fulfilled, (state, action) => {
        const result = action.payload;

        const newAdminCoursePermissionTemplateListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminCoursePermissionTemplateListData = newAdminCoursePermissionTemplateListData;
      })
      .addCase(
        adminGetCoursePermissionTemplatesForDropDown.fulfilled,
        (state, action) => {
          const result = action.payload;

          const newAdminCoursePermissionTemplateListDataForDropdown = {
            limit: result.limit,
            offset: result.offset,
            totalDocs: result.totalDocs || 0,
            list: result.docs || [],
          };

          state.adminCoursePermissionTemplateListDataForDropdown = newAdminCoursePermissionTemplateListDataForDropdown;
        }
      )
      .addCase(
        adminCreateCoursePermissionTemplate.fulfilled,
        (state, action) => {
          const result = action.payload;

          if (state.adminCoursePermissionTemplateListData) {
            const newAdminCoursePermissionTemplateListData = {
              ...state.adminCoursePermissionTemplateListData,
            };

            newAdminCoursePermissionTemplateListData.list.unshift(result);

            if (newAdminCoursePermissionTemplateListData.totalDocs) {
              newAdminCoursePermissionTemplateListData.totalDocs += 1;
            } else {
              newAdminCoursePermissionTemplateListData.totalDocs = 1;
            }

            state.adminCoursePermissionTemplateListData = newAdminCoursePermissionTemplateListData;
          }
        }
      )
      .addCase(
        adminUpdateCoursePermissionTemplate.fulfilled,
        (state, action) => {
          const result = action.payload;

          if (
            state.adminCoursePermissionTemplateListData &&
            state.adminCoursePermissionTemplateListData.list &&
            state.adminCoursePermissionTemplateListData.list.length
          ) {
            const newAdminCoursePermissionTemplateListData = {
              ...state.adminCoursePermissionTemplateListData,
            };

            const index = newAdminCoursePermissionTemplateListData.list.findIndex(
              (item) => item._id === result._id
            );

            if (index > -1) {
              newAdminCoursePermissionTemplateListData.list[index] = result;
            }

            state.adminCoursePermissionTemplateListData = newAdminCoursePermissionTemplateListData;
          }
        }
      )
      .addCase(
        adminDeleteCoursePermissionTemplate.fulfilled,
        (state, action) => {
          const result = action.payload;

          if (
            state.adminCoursePermissionTemplateListData &&
            state.adminCoursePermissionTemplateListData.list &&
            state.adminCoursePermissionTemplateListData.list.length
          ) {
            const newAdminCoursePermissionTemplateListData = {
              ...state.adminCoursePermissionTemplateListData,
            };

            newAdminCoursePermissionTemplateListData.totalDocs = newAdminCoursePermissionTemplateListData.totalDocs
              ? newAdminCoursePermissionTemplateListData.totalDocs - 1
              : 0;

            newAdminCoursePermissionTemplateListData.list = newAdminCoursePermissionTemplateListData.list.filter(
              (item) => item._id !== result._id
            );

            state.adminCoursePermissionTemplateListData = newAdminCoursePermissionTemplateListData;
          }
        }
      );

    // Common Matchers
    builder.addMatcher(
      isFulfilled(
        adminCreateCoursePermissionTemplate,
        adminGetCoursePermissionTemplates,
        adminUpdateCoursePermissionTemplate,
        adminDeleteCoursePermissionTemplate,
        adminVerifyCoursePermissionTemplateDelete
      ),
      (state) => {
        state.status = 'idle';
      }
    );

    builder.addMatcher(
      isPending(
        adminCreateCoursePermissionTemplate,
        adminGetCoursePermissionTemplates,
        adminUpdateCoursePermissionTemplate,
        adminDeleteCoursePermissionTemplate,
        adminVerifyCoursePermissionTemplateDelete
      ),
      (state) => {
        state.status = 'loading';
      }
    );

    builder.addMatcher(
      isRejected(
        adminCreateCoursePermissionTemplate,
        adminGetCoursePermissionTemplates,
        adminUpdateCoursePermissionTemplate,
        adminDeleteCoursePermissionTemplate,
        adminVerifyCoursePermissionTemplateDelete
      ),
      (state) => {
        state.status = 'rejected';
      }
    );
  },
});

export const getCoursePermissionTemplatesState = (
  state: RootState
): RootState['coursePermissionTemplates'] => state.coursePermissionTemplates;

export const {
  // COURSE PERMISSION TEMPLATES
  resetAdminCoursePermissionTemplateListData,
  resetAdminCoursePermissionTemplateListDataForDropdown,
  resetAdminCoursePermissionTemplateDeleteVerificationData,
} = SettingsSlice.actions;

export * from './adminApi';
export default SettingsSlice.reducer;
